import { FunctionComponent, memo, MouseEvent, useCallback, useRef, useState } from "react";
import { Box } from "../../box";
import { Stack } from "../../stack";
import { Text } from "../../text";
import { Tooltip } from "../../tooltip";
import type { IProps } from "./grouped-list-option.types";
import styles from "./grouped-list-option.module.scss";

/**
 * An option in a `GroupedList`: its title, an optional subtitle, and its description cut to two lines, styled as a menu
 * item (the shared active style and marker, which the pointer moves too). A description cut short shows in full in a
 * tooltip on hover. When the keyboard moved the active option here, it is ringed, as the `Select` rings its
 * keyboard-highlighted option.
 * Pressing it never takes focus, so typing stays in the list's input.
 * @param props.id               The option's element id, for the input's `aria-activedescendant`.
 * @param props.value            The item's id, passed back to `onChoose` and `onHover`.
 * @param props.title            The first line.
 * @param props.subtitle         An optional second line.
 * @param props.description      The description, cut to two lines.
 * @param props.isActive         Whether the option is the active one, which Enter chooses.
 * @param props.isKeyboardActive Whether the keyboard, rather than the pointer, made it active.
 * @param props.isDisabled       Whether it can't be chosen yet: it shows disabled, and the pointer neither marks nor
 *                               chooses it.
 * @param props.onChoose         Choose the option.
 * @param props.onHover          Make the option the active one.
 * @returns The option.
 */
const GroupedListOption: FunctionComponent<IProps> = ({
    id,
    value,
    title,
    subtitle,
    description,
    isActive,
    isKeyboardActive = false,
    isDisabled = false,
    onChoose,
    onHover,
}) => {
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const keepFocus = useCallback((event: MouseEvent) => event.preventDefault(), []);
    const choose = useCallback(() => onChoose(value), [onChoose, value]);
    const hover = useCallback(() => onHover(value), [onHover, value]);

    // Measured on hover, since only a description cut short by the line clamp needs its tooltip.
    const measureDescription = useCallback(() => {
        const element = descriptionRef.current;
        setIsTruncated(element !== null && element.scrollHeight > element.clientHeight);
    }, []);

    return (
        <Tooltip label={description} position="right" w={320} disabled={!isTruncated}>
            <Box
                id={id}
                role="option"
                aria-selected={isActive}
                aria-disabled={isDisabled || undefined}
                data-active={isActive}
                data-disabled={isDisabled || undefined}
                data-keyboard-active={(isActive && isKeyboardActive) || undefined}
                className={styles.option}
                pl="md"
                // Clears the active marker, which sits 10-13px in from the right edge (see `MenuItemActiveMarker`).
                pr="xl"
                py="md"
                bdrs="md"
                onMouseDown={keepFocus}
                onMouseMove={isDisabled ? undefined : hover}
                onMouseEnter={measureDescription}
                onClick={isDisabled ? undefined : choose}
            >
                <Stack gap="xs">
                    {title}
                    {subtitle}
                    {typeof description !== "undefined" && description !== "" && (
                        <Text ref={descriptionRef} size="xs" dimmed lineClamp={2}>
                            {description}
                        </Text>
                    )}
                </Stack>
            </Box>
        </Tooltip>
    );
};

export default memo(GroupedListOption);
